/**
 * Copyright (c) 2010-2022 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { concatMap, concatMapTo, filter, map, switchMapTo, take, tap, withLatestFrom } from 'rxjs/operators';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { Navigate } from '@ngxs/router-plugin';
import { AppPageRoute } from '../../state/appConfig/app-config.model';
import { PageState } from '../../state/page/page.state';
import { AuthenticatedGuard } from './authenticated.guard';

@Injectable()
export class RootUrlGuard  {
    pageStateLoaded$: Observable<boolean> = inject(Store).select(PageState.loaded);
    defaultPage$: Observable<string> = inject(Store).select(PageState.defaultPage);
    enabledPages$: Observable<AppPageRoute[]> = inject(Store).select(PageState.enabledPages);

    constructor(
        private logger: LoggerService,
        private authenticatedGuard: AuthenticatedGuard,
        private router: Router,
        private store: Store
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean|UrlTree> {
        return this.authenticatedGuard.canActivate(route, state).pipe(
            filter((b) => b),
            concatMap(() => this.pageStateLoaded$),
            filter((b) => b),
            map(() => state.url.split('?')[0] !== '/'),
            withLatestFrom(this.defaultPage$),
            take(1),
            map(([canActivate, defaultPage]) => {
                if (!canActivate) {
                    // zoomGoto from url always goes to map
                    if (route.queryParams['layer'] && route.queryParams['key']) {
                        return this.router.createUrlTree(['/map'], route.queryParams);
                    } else {
                        return this.router.createUrlTree([`/${defaultPage}`]);
                    }
                }
                return canActivate;
            })
        );
    }
}
